import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { trustOrgs } from '../data/casinos';
import PageHero from '../components/ui/PageHero';
import './ResponsibleGambling.css';

const warningSignals = [
  'Du bruger mere tid på spil end planlagt',
  'Du spiller for at genvinde tidligere tab',
  'Du skjuler din spilaktivitet for Familie og venner',
  'Du er rastløs eller irritabel, når du ikke kan spille',
  'Du har lånt penge for at finansiere dit spil',
  'Spil påvirker dit arbejde, skole eller relationer',
  'Du tænker konstant på dit næste spil',
  'Du har forsøgt at stoppe eller begrænse spil uden held',
];

const safetyTools = [
  {
    icon: '⏱️',
    title: 'Tidsgrænser',
    desc: 'Sæt en daglig eller ugentlig tidsgrænse for din spilsession direkte i casinoets kontoindstillinger.'
  },
  {
    icon: '💶',
    title: 'Indskudsgrænser',
    desc: 'Begræns, hvor meget du kan indbetale per dag, uge eller måned. Dette er din stærkeste beskyttelse.'
  },
  {
    icon: '⏸️',
    title: 'Spillepause',
    desc: 'Tag en pause fra 24 timer op til 6 måneder. Alle licenserede casinoer skal tilbyde denne mulighed.'
  },
  {
    icon: '🔒',
    title: 'Selvudelukkelse',
    desc: 'Udeluk dig permanent eller for en lang periode fra et enkelt casino eller alle danske casinoer via ROFUS.'
  },
  {
    icon: '📊',
    title: 'Aktivitetsoversigt',
    desc: 'Se din spilhistorik, tid brugt og tab/gevinst over tid. Indsigt er første skridt mod kontrol.'
  },
  {
    icon: '🔔',
    title: 'Realitetscheck',
    desc: 'Aktivér automatiske påmindelser om, hvor længe du har spillet. Casinoer med dansk licens er forpligtede til dette.'
  },
];

export default function ResponsibleGambling() {
  return (
    <>
      <Helmet>
        <title>Ansvarligt Spil i Danmark – TopCasinoDansk</title>
        <meta name="description" content="Ressourcer og vejledning om ansvarligt spil i Danmark. Læs om ROFUS, StopSpillet, Spillemyndigheden og de redskaber, der beskytter dig som spiller." />
      </Helmet>

      <PageHero
        title="Ansvarligt Spil i Danmark"
        subtitle="Spil er underholdning – ikke en indkomstkilde. Her finder du ressourcer, vejledning og redskaber til et sundt og kontrolleret spilforhold."
        breadcrumb={<><Link to="/">Forside</Link> › Ansvarligt Spil</>}
      />

      {/* Emergency CTA */}
      <div className="rg-emergency-bar">
        <div className="container">
          <div className="rg-emergency-inner">
            <span className="rg-emergency-icon">⚠️</span>
            <div>
              <strong>Har du brug for øjeblikkelig hjælp?</strong>
              <span> Ring til StopSpillet på <a href="tel:70222825">70 22 28 25</a> – gratis og anonym hjælp.</span>
            </div>
            <a href="https://www.stopspillet.dk/" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
              Besøg StopSpillet.dk
            </a>
            <a href="https://www.rofus.nu/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-white btn-sm">
              Selvudeluk via ROFUS
            </a>
          </div>
        </div>
      </div>

      {/* Intro section */}
      <section className="rg-intro section">
        <div className="container">
          <div className="rg-intro-grid">
            <div>
              <div className="divider"></div>
              <h2>Forstå risikoen</h2>
              <p>
                Online casino og spil med penge kan for de fleste mennesker være en harmløs form for underholdning,
                men for en minoritet kan spil udvikle sig til et alvorligt problem, der påvirker økonomi, relationer
                og mental sundhed.
              </p>
              <p>
                Det er vigtigt at forstå, at casinospil er designet med en matematisk fordel til huset –
                de fleste spillere taber penge på lang sigt. Spil bør betragtes som betaling for underholdning,
                ikke som en måde at tjene penge på.
              </p>
              <p>
                I Danmark er spil reguleret af Spillemyndigheden, som pålægger alle licenserede operatører
                at tilbyde ansvarlige spilredskaber og integrere med ROFUS.
              </p>
            </div>
            <div className="rg-key-facts">
              <div className="rg-fact">
                <span className="rg-fact-num">18+</span>
                <span className="rg-fact-label">Minimumsalder for spil i Danmark</span>
              </div>
              <div className="rg-fact">
                <span className="rg-fact-num">3–5%</span>
                <span className="rg-fact-label">Estimeret andel spillere med problematisk spilmønster</span>
              </div>
              <div className="rg-fact">
                <span className="rg-fact-num">70.000+</span>
                <span className="rg-fact-label">Registrerede i ROFUS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning signals */}
      <section className="rg-warning section bg-sand">
        <div className="container">
          <div className="rg-warning-grid">
            <div>
              <div className="divider"></div>
              <h2>Advarselssignaler</h2>
              <p className="section-subtitle">
                Spilproblemer udvikler sig gradvist. Her er de mest almindelige advarselssignaler –
                er du i tvivl, bør du søge rådgivning.
              </p>
            </div>
            <div className="rg-signals-list">
              {warningSignals.map((signal, i) => (
                <div key={i} className="rg-signal-item">
                  <span className="rg-signal-dot" aria-hidden="true"></span>
                  <span>{signal}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rg-warning-cta">
            <p>
              Genkender du dig selv i flere af disse tegn? Du er ikke alene – og der er hjælp at hente.
            </p>
            <a href="https://www.stopspillet.dk/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Søg hjælp på StopSpillet.dk
            </a>
          </div>
        </div>
      </section>

      {/* Safety tools */}
      <section className="rg-tools section">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.75rem' }}>Tilgængelige spilsikkerhedsredskaber</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Alle licenserede casinoer i Danmark er forpligtede til at tilbyde disse redskaber.
            Brug dem aktivt – det er din ret som spiller.
          </p>

          <div className="rg-tools-grid">
            {safetyTools.map((tool, i) => (
              <div key={i} className="rg-tool-card">
                <div className="rg-tool-icon">{tool.icon}</div>
                <h4>{tool.title}</h4>
                <p>{tool.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Orgs */}
      <section className="rg-orgs section bg-sand">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.75rem' }}>Officielle hjælpeorganisationer i Danmark</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Disse organisationer er ikke tilknyttet TopCasinoDansk. Vi præsenterer dem som vigtige informationsressourcer
            for spillere og pårørende.
          </p>

          <div className="rg-orgs-grid">
            {trustOrgs.map((org) => (
              <div key={org.id} className="rg-org-card">
                <div className="rg-org-logo-wrap">
                  <img src={org.logo} alt={org.name} className="rg-org-logo" />
                </div>
                <div className="rg-org-content">
                  <h3>{org.name}</h3>
                  <p>{org.description}</p>
                  <a href={org.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
                    Besøg {org.name}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* ROFUS guide */}
          <div className="rg-rofus-guide">
            <h3>Sådan bruger du ROFUS</h3>
            <div className="rg-rofus-steps">
              <div className="rg-rofus-step">
                <span className="rg-rofus-step-num">1</span>
                <div>
                  <strong>Gå til ROFUS.nu</strong>
                  <p>Besøg den officielle hjemmeside og klik på "Tilmeld dig ROFUS".</p>
                </div>
              </div>
              <div className="rg-rofus-step">
                <span className="rg-rofus-step-num">2</span>
                <div>
                  <strong>Log ind med MitID</strong>
                  <p>Registreringen kræver MitID for at verificere din identitet.</p>
                </div>
              </div>
              <div className="rg-rofus-step">
                <span className="rg-rofus-step-num">3</span>
                <div>
                  <strong>Vælg udelukkelsesperiode</strong>
                  <p>Vælg en periode fra 1 år til permanent udelukkelse. Effekten er øjeblikkelig.</p>
                </div>
              </div>
              <div className="rg-rofus-step">
                <span className="rg-rofus-step-num">4</span>
                <div>
                  <strong>Udelukkelsen gælder alle</strong>
                  <p>Alle licenserede danske casinoer er forpligtede til at kontrollere og respektere ROFUS-registreringer.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Danish regulations */}
      <section className="rg-legal section">
        <div className="container">
          <div className="rg-legal-grid">
            <div>
              <div className="divider"></div>
              <h2>Dansk spilregulering</h2>
              <p>
                Det danske spillemarked er reguleret af Spillemyndigheden, der hører under Skatteministeriet.
                Reguleringsrammerne er fastsat i Spilleloven og pålægger alle licenserede operatører en lang
                række krav til spillerbeskyttelse.
              </p>
              <p>Operatører med dansk licens er bl.a. forpligtede til at:</p>
              <ul className="rg-legal-list">
                <li>Verificere spillernes alder ved oprettelse</li>
                <li>Kontrollere ROFUS ved login og indbetaling</li>
                <li>Tilbyde tids- og indskudsgrænser</li>
                <li>Tilbyde selvudelukkelse og spillepause</li>
                <li>Vise realtidsoplysninger om tab og tid brugt</li>
                <li>Sende periodiske realitetschecks til aktive spillere</li>
                <li>Bidrage til Spilfonden, der finansierer behandling af spilafhængighed</li>
              </ul>
            </div>
            <div className="rg-legal-aside">
              <div className="rg-important-reminder">
                <h4>Vigtigt at huske</h4>
                <ul>
                  <li>Spil er underholdning – ikke investeringer</li>
                  <li>Huset vinder altid på lang sigt</li>
                  <li>Spil aldrig med penge du ikke har råd at tabe</li>
                  <li>Lån aldrig penge til spil</li>
                  <li>Spil aldrig som flugt fra problemer</li>
                  <li>18+ – Mindreårige må ikke spille</li>
                </ul>
                <Link to="/" className="btn btn-outline" style={{ marginTop: '1.25rem', width: '100%', justifyContent: 'center' }}>
                  Tilbage til forsiden
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
